import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import type {EditMealCommand} from '../../domain/edit-meal-command';
import {FOOD_QUANTITY_UNITS} from '../../domain/food-quantity.value-object';
import {MEAL_TYPES} from '../../domain/meal-type.value-object';
import {MAX_NUTRITION_PER_100G} from '../../domain/nutrition-per-100g.value-object';

const MAX_DISPLAY_NAME = 200;
const MAX_ENTRIES = 50;
const MAX_GRAM_EQUIVALENT = 10_000;
const MAX_KCAL = 10_000;
const MAX_MACRO_GRAMS = 1_000;
const MAX_NOTES = 2_000;
const MAX_QUANTITY = 10_000;

const nutritionPer100gSchema = z.strictObject({
  kcal: z.number().finite().min(0).max(MAX_NUTRITION_PER_100G),
  proteinGrams: z.number().finite().min(0).max(MAX_NUTRITION_PER_100G),
  carbsGrams: z.number().finite().min(0).max(MAX_NUTRITION_PER_100G),
  fatGrams: z.number().finite().min(0).max(MAX_NUTRITION_PER_100G),
});

const entrySchema = z.strictObject({
  displayName: z.string().trim().min(1).max(MAX_DISPLAY_NAME),
  sourceFoodId: z.null(),
  source: z.string().trim().min(1),
  quantity: z.number().finite().min(0).max(MAX_QUANTITY),
  unit: z.enum(FOOD_QUANTITY_UNITS),
  gramEquivalent: z.number().finite().min(0).max(MAX_GRAM_EQUIVALENT).nullable(),
  nutritionPer100g: nutritionPer100gSchema.nullable(),
  kcal: z.number().finite().min(0).max(MAX_KCAL),
  proteinGrams: z.number().finite().min(0).max(MAX_MACRO_GRAMS),
  carbsGrams: z.number().finite().min(0).max(MAX_MACRO_GRAMS),
  fatGrams: z.number().finite().min(0).max(MAX_MACRO_GRAMS),
  notes: z.string().max(MAX_NOTES).nullable(),
});

export class EditMealCommandValidator implements Validator<EditMealCommand> {
  readonly standardSchema = z.strictObject({
    mealType: z.enum(MEAL_TYPES),
    consumedAt: z.iso.datetime({offset: true}).optional(),
    entries: z.array(entrySchema).min(1).max(MAX_ENTRIES),
  });

  validate(value: unknown): Result<EditMealCommand, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);

    if (!parsed.success) {
      return err(new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid edit meal body.'));
    }

    return ok(parsed.data);
  }
}
